import type { FetchParams, PaginatedResponse } from "@nuxt/app";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  WebhookEvent,
  Order,
  OrderDetail,
  Product,
  PaymentProcessor,
  Coupon,
  CreateProviderSubscriptionPlanInput,
  CreateProviderSubscriptionPlanResponse,
  CreateProductInput,
  CreateProductResponse,
  CreatePaymentProcessorResponse,
  CreatePaymentProcessorInput,
  SubscriptionPlanResponse,
  UpdateSubscriptionPlanInput,
  CreateCouponInput,
  UpdateCouponInput,
  CreateSubscriptionPlanInput,
  PaymentSimulationCatalogResponse,
  PaymentSimulationRequest,
  PaymentSimulationResponse,
  TeacherAccountSimulationRequest,
  TeacherAccountSimulationResponse,
} from "~/types/admin/payment";

class AdminPaymentModule extends HttpFactory {
  async getSubscriptionPlans(
    fetchParams: FetchParams,
  ): Promise<PaginatedResponse<SubscriptionPlanResponse>> {
    return await this.get<PaginatedResponse<SubscriptionPlanResponse>>(
      `${ENDPOINTS.ADMIN.PAYMENT.SUBSCRIPTION_PLANS}`,
      fetchParams,
    );
  }

  async getSubscriptionPlan(id: string): Promise<SubscriptionPlanResponse> {
    return await this.get<SubscriptionPlanResponse>(
      `${ENDPOINTS.ADMIN.PAYMENT.SUBSCRIPTION_PLANS}${id}/`,
    );
  }

  async createSubscriptionPlan(
    data: CreateSubscriptionPlanInput,
  ): Promise<SubscriptionPlanResponse> {
    return await this.post<SubscriptionPlanResponse>(
      `${ENDPOINTS.ADMIN.PAYMENT.SUBSCRIPTION_PLANS}`,
      data,
    );
  }

  async updateSubscriptionPlan(
    subscriptionPlanId: string,
    data: UpdateSubscriptionPlanInput,
  ): Promise<SubscriptionPlanResponse> {
    return await this.put<SubscriptionPlanResponse>(
      `${ENDPOINTS.ADMIN.PAYMENT.SUBSCRIPTION_PLANS}${subscriptionPlanId}/`,
      data,
    );
  }

  async getProviderSubscriptionPlans(): Promise<Product[]> {
    return await this.get<Product[]>(
      `${ENDPOINTS.ADMIN.PAYMENT.PROVIDER_SUBSCRIPTION_PLANS}`,
    );
  }

  async createProviderSubscriptionPlan(
    data: CreateProviderSubscriptionPlanInput,
  ): Promise<CreateProviderSubscriptionPlanResponse> {
    return await this.post<CreateProviderSubscriptionPlanInput>(
      `${ENDPOINTS.ADMIN.PAYMENT.PROVIDER_SUBSCRIPTION_PLANS}`,
      data,
    );
  }

  async createProduct(
    data: CreateProductInput,
  ): Promise<CreateProductResponse> {
    return await this.post<CreateProductInput>(
      `${ENDPOINTS.ADMIN.PAYMENT.PRODUCTS}`,
      data,
    );
  }

  async createPaymentProcessor(
    data: CreatePaymentProcessorInput,
  ): Promise<CreatePaymentProcessorResponse> {
    return await this.post<CreatePaymentProcessorInput>(
      `${ENDPOINTS.ADMIN.PAYMENT.PAYMENT_PROCESSORS}`,
      data,
    );
  }

  async getWebhookEvents(
    fetchParams: FetchParams,
  ): Promise<PaginatedResponse<WebhookEvent>> {
    return await this.get<PaginatedResponse<WebhookEvent>>(
      `${ENDPOINTS.ADMIN.WEBHOOKS.WEBHOOK_EVENTS}`,
      fetchParams,
    );
  }

  async getWebhookEvent(webhookEventId: string): Promise<WebhookEvent> {
    return await this.get<WebhookEvent>(
      `${ENDPOINTS.ADMIN.WEBHOOKS.WEBHOOK_EVENTS}${webhookEventId}`,
    );
  }

  async getOrders(fetchParams: FetchParams): Promise<PaginatedResponse<Order>> {
    return await this.get<PaginatedResponse<Order>>(
      `${ENDPOINTS.ADMIN.PAYMENT.ORDERS}`,
      fetchParams,
    );
  }

  async getOrder(orderId: string): Promise<OrderDetail> {
    return await this.get<OrderDetail>(
      `${ENDPOINTS.ADMIN.PAYMENT.ORDERS}${orderId}/`,
    );
  }

  async getProducts(): Promise<Product[]> {
    return await this.get<Product[]>(`${ENDPOINTS.ADMIN.PAYMENT.PRODUCTS}`);
  }

  async getPaymentProcessors(): Promise<PaymentProcessor[]> {
    return await this.get<PaymentProcessor[]>(
      `${ENDPOINTS.ADMIN.PAYMENT.PAYMENT_PROCESSORS}`,
    );
  }

  async getCoupons(
    fetchParams: FetchParams,
  ): Promise<PaginatedResponse<Coupon>> {
    return await this.get<PaginatedResponse<Coupon>>(
      `${ENDPOINTS.ADMIN.PAYMENT.COUPONS}`,
      fetchParams,
    );
  }

  async getCoupon(couponId: string): Promise<Coupon> {
    return await this.get<Coupon>(
      `${ENDPOINTS.ADMIN.PAYMENT.COUPONS}${couponId}/`,
    );
  }

  async createCoupon(data: CreateCouponInput): Promise<Coupon> {
    return await this.post<CreateCouponInput>(
      `${ENDPOINTS.ADMIN.PAYMENT.COUPONS}`,
      data,
    );
  }

  async updateCoupon(
    couponId: string,
    data: UpdateCouponInput,
  ): Promise<Coupon> {
    return await this.put<UpdateCouponInput>(
      `${ENDPOINTS.ADMIN.PAYMENT.COUPONS}${couponId}/`,
      data,
    );
  }

  async deleteCoupon(couponId: string): Promise<void> {
    return await this.delete<void>(
      `${ENDPOINTS.ADMIN.PAYMENT.COUPONS}${couponId}/`,
    );
  }

  async simulatePayment(
    data: PaymentSimulationRequest,
  ): Promise<PaymentSimulationResponse> {
    return await this.post<PaymentSimulationResponse>(
      `${ENDPOINTS.ADMIN.PAYMENT.SIMULATIONS}`,
      data,
    );
  }

  async getSimulationCatalog(): Promise<PaymentSimulationCatalogResponse> {
    return await this.get<PaymentSimulationCatalogResponse>(
      `${ENDPOINTS.ADMIN.PAYMENT.SIMULATIONS_CATALOG}`,
    );
  }

  async simulateTeacherAccount(
    data: TeacherAccountSimulationRequest,
  ): Promise<TeacherAccountSimulationResponse> {
    return await this.post<TeacherAccountSimulationResponse>(
      `${ENDPOINTS.ADMIN.PAYMENT.TEACHER_ACCOUNT_SIMULATIONS}`,
      data,
    );
  }
}

export default AdminPaymentModule;
